import { createContext, useContext, useEffect, useState, useCallback } from "react";
import LS_API from "../api/axios";
import { getAccessToken } from "../api/axios";

export const PermissionContext = createContext();

export const PermissionProvider = ({ children }) => {
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [provisioning, setProvisioning] = useState(false);
  const [hasCheckedOnce, setHasCheckedOnce] = useState(false);

  // ✅ NEW: User profile state
  const [userProfile, setUserProfile] = useState(null);

  // Track sync attempts to prevent infinite retries
  const [syncAttempts, setSyncAttempts] = useState(0);
  const MAX_SYNC_ATTEMPTS = 2;

  const fetchUserProfile = useCallback(async () => {
    const token = getAccessToken();
    if (!token) {
      console.warn("No access token found, skipping user profile fetch");
      setLoading(false);
      setHasCheckedOnce(true);
      return;
    }

    setLoading(true);
    try {
      const res = await LS_API.get("/api/users/me");
      console.log("User profile API response:", res.data);

      if (res.data && typeof res.data === 'object') {
        const { permissions: userPermissions = [], ...userData } = res.data;

        setUserProfile({
          id: userData.username, // Using username as ID for now
          username: userData.username,
          email: userData.email,
          fullName: userData.fullName,
        });

        // Store permissions
        setPermissions(userPermissions || []);

        console.log("User profile updated:", userData);
        console.log("Permissions updated:", userPermissions);
      } else {
        // Fallback for old format or empty response
        console.warn("Unexpected API response format:", res.data);
        setUserProfile(null);
        setPermissions([]);
      }

    } catch (err) {
      console.error("Failed to fetch user profile", err);
      setUserProfile(null);
      setPermissions([]);
    } finally {
      setLoading(false);
      setHasCheckedOnce(true);
    }
  }, [syncAttempts]);

  const syncAndReload = useCallback(async () => {
    if (syncAttempts >= MAX_SYNC_ATTEMPTS) {
      console.log(`Max sync attempts (${MAX_SYNC_ATTEMPTS}) reached. Stopping.`);
      return;
    }

    console.log(`Sync attempt ${syncAttempts + 1}/${MAX_SYNC_ATTEMPTS}`);
    setProvisioning(true);
    setSyncAttempts(prev => prev + 1);

    try {
      await LS_API.post("/api/users/sync-permissions");
      await fetchUserProfile();
    } catch (error) {
      console.error("Sync failed:", error);
    } finally {
      setProvisioning(false);
    }
  }, [fetchUserProfile, syncAttempts]);

  // Load user profile on mount
  useEffect(() => {
    fetchUserProfile();
  }, [fetchUserProfile]);

  // Helper functions
  const hasPermission = useCallback((permission) =>
    permissions.some(p =>
      (typeof p === 'string' ? p : p.name) === permission
    ), [permissions]);

  const hasAnyPermission = useCallback((...perms) =>
    perms.some(permission => hasPermission(permission)), [hasPermission]);

  const hasAllPermissions = useCallback((...perms) =>
    perms.every(permission => hasPermission(permission)), [hasPermission]);

  // Check if user has specific role pattern
  const hasRole = useCallback((rolePattern) => {
    if (!userProfile) return false;
    // Check if user has permission that matches role pattern
    // e.g., rolePattern = "admin.*"
    const patternRegex = new RegExp(`^${rolePattern.replace('*', '.*')}$`);
    return permissions.some(permission => patternRegex.test(permission));
  }, [userProfile, permissions]);

  // Get user initials for avatar
  const getUserInitials = useCallback(() => {
    if (!userProfile?.fullName) return userProfile?.username?.charAt(0).toUpperCase() || 'U';
    return userProfile.fullName
      .split(' ')
      .map(name => name.charAt(0))
      .join('')
      .toUpperCase()
      .substring(0, 2);
  }, [userProfile]);

  const hasExhaustedRetries = syncAttempts >= MAX_SYNC_ATTEMPTS;

  return (
    <PermissionContext.Provider
      value={{
        permissions,
        loading,
        provisioning,
        syncAndReload,
        hasPermission,
        hasAnyPermission,
        hasAllPermissions,
        reloadPermissions: fetchUserProfile,
        hasCheckedOnce,
        syncAttempts,
        maxSyncAttempts: MAX_SYNC_ATTEMPTS,
        hasExhaustedRetries,
        userProfile,
        isAuthenticated: !!userProfile,
        getUserInitials,
        hasRole,

        // Convenience methods for common permission checks
        isAdmin: hasPermission('admin.permissions.edit') || hasPermission('admin.*'),
        canEditPermissions: hasPermission('admin.permissions.edit'),
        canViewDocuments: hasPermission('ls.documents.view'),
        canViewPowerBI: (dashboard) => hasPermission(`powerbi.${dashboard}.view`),

        // Update user profile (if needed)
        updateUserProfile: (updates) => {
          setUserProfile(prev => ({
            ...prev,
            ...updates
          }));
        }
      }}
    >
      {children}
    </PermissionContext.Provider>
  );
};

export const usePermissions = () => useContext(PermissionContext);